import React, { useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import './DocViewer.scss';
import { Icon } from '../../../components';
import DocuMateScanService from '../../DocuMateScan/DocuMateScanService';
import { getRefiDocument } from '../../services/DocuMateAPI';
import { DOC_VIEWER_FIRST_PAGE, DOC_VIEWER_LAST_PAGE, DOC_VIEWER_GO_TO_PAGE, DOC_VIEWER_FIRST_PAGE_TITLE, DOC_VIEWER_LAST_PAGE_TITLE, DOC_VIEWER_PREVIOUS_PAGE_TITLE, DOC_VIEWER_NEXT_PAGE_TITLE, DOC_VIEWER_GO_TO_PAGE_TITLE } from '../util/docuMateLabel';
// import 'react-pdf/dist/esm/Page/TextLayer.css'; 

// Set up the worker path for pdf.js
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

interface DocViewerProps {
    document: Blob;
    scanId: string;
    hidePagination?: boolean;
    initialScale?: number;
    onLoadError?: (error: Error) => void;
}

const DocViewer: React.FC<DocViewerProps> = ({ document, scanId, hidePagination = false, initialScale = 1.2, onLoadError }) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(initialScale); // Scale for zoom
    const [rotation, setRotation] = useState(0); // Rotation angle
    const [currentDocument, setCurrentDocument] = useState<string | null>(null);
    const [pageRotations, setPageRotations] = useState<{ [key: number]: number }>({});
    const [fileName, setFileName] = useState<string>(scanId);

    const initializeDocument = async () => {
        try {
            const blob = document;
            const blobUrl = URL.createObjectURL(blob);
            setCurrentDocument(blobUrl);
            setFileName(scanId);
        } catch (error) {
            console.error('Error fetching the PDF file:', error);
        }
    };

    useEffect(() => {
        initializeDocument();

        // Cleanup function to revoke blob URL
        return () => {
            if (currentDocument) {
                URL.revokeObjectURL(currentDocument);
            }
        };
    }, [document]);

    const onDocumentLoadSuccess = async (pdf: any) => {
        setNumPages(pdf.numPages);
        setPageNumber(1); // Start with the first page

        // Fetch the rotation metadata for each page
        const rotations: { [key: number]: number } = {};
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            rotations[i] = page.rotate || 0; // Default to 0 if no rotation metadata exists
        }
        setPageRotations(rotations);
    };

    // Editing features' functions
    const nextPage = () => setPageNumber((prevPage) => Math.min(prevPage + 1, numPages || 1));
    const prevPage = () => setPageNumber((prevPage) => Math.max(prevPage - 1, 1));
    const gotoFirstPage = () => setPageNumber(1);
    const gotoLastPage = () => setPageNumber(numPages || 1);
    const gotoPage = (page: number) => {
        if (page >= 1 && page <= (numPages || 1)) {
            setPageNumber(page);
        }
    };
    const zoomIn = () => setScale((prevScale) => prevScale + 0.2);
    const zoomOut = () => setScale((prevScale) => Math.max(prevScale - 0.2, 0.5));
    const rotateClockwise = () => {
        setRotation((prevRotation) => (prevRotation + 90) % 360);
    };

    const rotateCounterClockwise = () => {
        setRotation((prevRotation) => (prevRotation - 90 + 360) % 360);
    };

    const getPageRotation = (pageNumber: number) => {
        const pageRotation = pageRotations[pageNumber] || 0; // Default to 0 if no rotation metadata exists
        return (pageRotation + rotation) % 360;
    };
    
    const handleDownload = () => {
        if (!document) return;
        const blobUrl = URL.createObjectURL(document); 
        const globalDocument = window.document; 
        const link = globalDocument.createElement('a');
        link.href = blobUrl;        
        const isPdf = document.type === 'application/pdf';
        const hasPdfExtension = fileName.toLowerCase().endsWith('.pdf');        
        link.download = hasPdfExtension || isPdf ? fileName : `${fileName}.pdf`;
        globalDocument.body.appendChild(link);
        link.click();
        globalDocument.body.removeChild(link);
        URL.revokeObjectURL(blobUrl); // Clean up
    };

    return (
        <div className="doc-viewer-container">
            <div className="viewer-container">
                <div className="doc-viewer-header">
                    <span style={{ fontWeight: 'bold' }}>{fileName}</span>
                </div>
                <div className="doc-viewer">
                    {document && (
                        <Document
                            file={document}
                            onLoadSuccess={onDocumentLoadSuccess} // Use the parsed PDF.js object
                            onLoadError={onLoadError}
                        >
                            <Page
                                pageNumber={pageNumber}
                                scale={scale}
                                rotate={getPageRotation(pageNumber)} // Normalize rotation for the current page
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        </Document>
                    )}
                </div>
            </div>
            <div className="toolbar">
                {!hidePagination && (
                    <>
                        <button onClick={gotoFirstPage} disabled={pageNumber <= 1} title={DOC_VIEWER_FIRST_PAGE_TITLE}>
                            <span>{DOC_VIEWER_FIRST_PAGE}</span>
                        </button>
                        <button onClick={prevPage} disabled={pageNumber <= 1} title={DOC_VIEWER_PREVIOUS_PAGE_TITLE}>
                            <span>
                                <Icon icon="prevPage" className="image-viewer-icons" />
                            </span>
                        </button>
                        <span className="pageDisplay">{pageNumber}/{numPages}</span>
                        <button onClick={nextPage} disabled={pageNumber >= (numPages || 1)} title={DOC_VIEWER_NEXT_PAGE_TITLE}>
                            <span>
                                <Icon icon="nextPage" className="image-viewer-icons" />
                            </span>
                        </button>
                        <button onClick={gotoLastPage} disabled={pageNumber >= (numPages || 1)} title={DOC_VIEWER_LAST_PAGE_TITLE}>
                            <span>{DOC_VIEWER_LAST_PAGE}</span>
                        </button>
                        <span className="pageDisplay">
                            | {DOC_VIEWER_GO_TO_PAGE}{" "}
                            <input
                                type="number"
                                defaultValue={pageNumber}
                                min={1}
                                max={numPages || 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) : 1;
                                    gotoPage(page);
                                }}
                                title={DOC_VIEWER_GO_TO_PAGE_TITLE}
                            />
                        </span>
                    </>
                )}

                <button onClick={zoomIn}>
                    <span>
                        <Icon icon="zoomIn" className="image-viewer-icons" />
                    </span>
                </button>
                <button onClick={zoomOut} disabled={scale <= 0.8}>
                    <span>
                        <Icon icon="zoomOut" className="image-viewer-icons" />
                    </span>
                </button>

                <button onClick={rotateClockwise}>
                    <span>
                        <Icon icon="rotateClockwise" className="image-viewer-icons" />
                    </span>
                </button>
                <button onClick={rotateCounterClockwise}>
                    <span>
                        <Icon icon="rotateCounterClockwise" className="image-viewer-icons" />
                    </span>
                </button>                
                <button onClick={handleDownload}>
                    <span>
                        <Icon icon="down-indication" className="image-viewer-icons" />
                    </span>
                </button>
            </div>
        </div>
    );
};

export default DocViewer;